import React from 'react';
import Typography from '@material-ui/core/Typography';
import MenuIcon from '@material-ui/icons/Menu';
import usePageTitle from 'hooks/usePageTitle';
import strings from 'strings';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import TitleLabel from 'UI/components/atoms/TitleLabel';
import ContentPageLayout from 'UI/components/templates/ContentPageLayout';
import { AppName } from 'UI/constants/defaults';

import { useDualPanelLayout } from './DualPanelLayout.hooks';
import { useStyles } from './DualPanelLayout.styled';

/**
 * @param {Object} props
 * @param {string} [props.storageKey]
 * @param {Object} props.leftPanel
 * @param {Object} props.rightPanel
 */
const DualPanelLayout = ({ leftPanel, rightPanel, storageKey }) => {
  const classes = useStyles();

  const { title, Header: LeftHeader, Actions: LeftActions } = leftPanel.header;
  const {
    HeaderTitle: RightHeaderTitle,
    Header: RightHeader,
    Actions: RightActions
  } = rightPanel.header;

  usePageTitle({ title: `${AppName} | ${title}` });

  const {
    containerRef,
    isExpanded,
    leftWidthPercentage,
    maxSplitPercent,
    minSplitPercent,
    rightWidthPercentage,
    splitPercentage,
    startDragging,
    toggleExpanded
  } = useDualPanelLayout({ storageKey });

  return (
    <ContentPageLayout mode="customLayout">
      <div ref={containerRef} className={classes.container}>
        <div className={classes.leftPanel} style={{ width: leftWidthPercentage }}>
          <div className={classes.panelContainer}>
            <div className={classes.leftHeaderContainer}>
              <div className={classes.leftHeader}>
                <TitleLabel text={title} />
                {LeftActions && <div>{LeftActions}</div>}
              </div>
              {LeftHeader}
            </div>
            <div className={classes.leftContent}>{leftPanel.Content}</div>
          </div>
        </div>

        {isExpanded && (
          <div
            aria-label={strings.layouts.dualPanel.draggableDivider}
            aria-orientation="vertical"
            aria-valuemax={maxSplitPercent}
            aria-valuemin={minSplitPercent}
            aria-valuenow={splitPercentage}
            className={classes.divider}
            onMouseDown={startDragging}
            role="slider"
            tabIndex={0}
            title={strings.layouts.dualPanel.draggableDivider}
          >
            <div className={classes.dividerHandle} />
          </div>
        )}

        <div className={classes.rightPanel} style={{ width: rightWidthPercentage }}>
          <div className={classes.panelContainer}>
            <div className={classes.rightHeaderContainer}>
              <div className={classes.rightHeader}>
                <FPIconButton
                  aria-label={strings.layouts.dualPanel.toggleLeftPanel}
                  onClick={toggleExpanded}
                >
                  <MenuIcon color="primary" />
                </FPIconButton>
                {RightHeaderTitle && (
                  <Typography variant="h5" className={classes.rightHeaderTitle}>
                    {RightHeaderTitle}
                  </Typography>
                )}
                {RightHeader && <div className={classes.rightHeaderTitle}>{RightHeader}</div>}
              </div>
              {RightActions && <div>{RightActions}</div>}
            </div>
            <div className={classes.rightContentContainer}>{rightPanel.Content}</div>
          </div>
        </div>
      </div>
    </ContentPageLayout>
  );
};

export default DualPanelLayout;
